export default class BlockSoundWave extends HTMLElement {
	constructor() {
		super();

		this.waveformPeaks = [];
		this.waveformBarHeights = [];
		this.$svg = null;

		this.resizeObserver = null;
		this.pointerX = null;
		this.width = 1;
		this.height = 1;

		this.onWaveformPointerMove = this.onWaveformPointerMove.bind(this);
		this.onWaveformPointerLeave = this.onWaveformPointerLeave.bind(this);
	}

	connectedCallback() {
		this.isAttached = true;

		this.$svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		this.$svg.setAttribute('aria-hidden', 'true');
		this.$svg.setAttribute('preserveAspectRatio', 'none');

		Object.assign(this.$svg.style, {
			display: 'block',
			width: '100%',
			height: '100%',
			overflow: 'visible',
		});

		this.innerHTML = '';
		this.appendChild(this.$svg);

		this.style.cursor = 'pointer';
		this.style.touchAction = 'none';

		this.addEventListener('pointermove', this.onWaveformPointerMove);
		this.addEventListener('pointerleave', this.onWaveformPointerLeave);

		this.resizeObserver = new ResizeObserver(() => {
			this.resize();
		});

		this.resizeObserver.observe(this);

		this.resize();
		this.update();
	}

	disconnectedCallback() {
		this.isAttached = false;

		this.removeEventListener('pointermove', this.onWaveformPointerMove);
		this.removeEventListener('pointerleave', this.onWaveformPointerLeave);

		this.resizeObserver?.disconnect();
	}

	getBarWidth() {
		return Number(this.getAttribute('audio-waveform-bar-width')) || 2;
	}

	getBarGap() {
		return Number(this.getAttribute('audio-waveform-bar-gap')) || 6;
	}

	generatePeaks() {
		const barWidth = this.getBarWidth();
		const barGap = this.getBarGap();

		const barCount = Math.max(
			1,
			Math.floor((this.width + barGap) / (barWidth + barGap))
		);

		this.waveformPeaks = Array.from({ length: barCount }, (_, index) => {
			const wave = Math.sin(index * 0.35) * 0.5 + 0.5;
			const detail = Math.sin(index * 1.7) * 0.25 + 0.25;

			return Math.max(0.12, Math.min(1, wave * 0.7 + detail));
		});

		this.waveformBarHeights = Array.from(
			{ length: barCount },
			(_, index) => this.waveformBarHeights[index] || barWidth
		);

		this.renderBars();
	}

	renderBars() {
		if (!this.$svg) {
			return;
		}

		this.$svg.innerHTML = '';

		this.waveformPeaks.forEach(() => {
			const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
			rect.setAttribute('fill', 'currentColor');
			this.$svg.appendChild(rect);
		});
	}

	resize() {
		const rect = this.getBoundingClientRect();

		this.width = Math.max(1, rect.width);
		this.height = Math.max(1, rect.height);

		if (this.$svg) {
			this.$svg.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);
		}

		this.generatePeaks();
	}

	onWaveformPointerMove(event) {
		const rect = this.getBoundingClientRect();
		this.pointerX = event.clientX - rect.left;
	}

	onWaveformPointerLeave() {
		this.pointerX = null;
	}

	update() {
		if (!this.isAttached) {
			return;
		}

		requestAnimationFrame(() => {
			this.update();
		});

		if (!this.$svg || !this.waveformPeaks.length) {
			return;
		}

		const barWidth = this.getBarWidth();
		const defaultBarGap = this.getBarGap();
		const barCount = this.waveformPeaks.length;

		const barGap =
			barCount > 1
				? Math.max(0, (this.width - barCount * barWidth) / (barCount - 1))
				: defaultBarGap;

		const centerY = this.height / 2;
		const maxBarHeight = this.height * 0.72;

		const cursorRadius = this.width * 0.2;
		const smoothing = 0.1;

		const rects = this.$svg.children;

		this.waveformPeaks.forEach((peak, index) => {
			const rect = rects[index];

			if (!rect) {
				return;
			}

			const x = index * (barWidth + barGap);
			const barCenterX = x + barWidth / 2;

			let influence = 0;

			if (this.pointerX !== null) {
				const distance = Math.abs(this.pointerX - barCenterX);
				influence = Math.max(0, 1 - distance / cursorRadius);
			}

			const dotHeight = barWidth;
			const fullHeight = peak * maxBarHeight;
			const targetHeight = dotHeight + (fullHeight - dotHeight) * influence;

			const currentHeight = this.waveformBarHeights[index] || dotHeight;
			const barHeight = currentHeight + (targetHeight - currentHeight) * smoothing;

			this.waveformBarHeights[index] = barHeight;

			const y = centerY - barHeight / 2;

			rect.setAttribute('x', x);
			rect.setAttribute('y', y);
			rect.setAttribute('width', barWidth);
			rect.setAttribute('height', barHeight);
			rect.setAttribute('rx', barWidth / 2);
			rect.setAttribute('ry', barWidth / 2);
		});
	}
}

customElements.define('block-sound-wave', BlockSoundWave);